/*
 * ===============================================================================================================
 *                                Copyright 2021, Blue Yonder Group, Inc.
 *                                           All Rights Reserved
 *
 *                               THIS IS UNPUBLISHED PROPRIETARY SOURCE CODE OF
 *                                          BLUE YONDER GROUP, INC.
 *
 *
 *                         The copyright notice above does not evidence any actual
 *                                 or intended publication of such source code.
 *
 * ===============================================================================================================
 */
import { useEffect, useCallback, useState, useMemo } from 'react';
import _ from "lodash";
import { Inbox } from '@jda/lui-common-icon-library-mui5';
import { useSelector, useDispatch } from 'react-redux';
import { LuiListCollector } from '@jda/lui-common-component-library-mui5';
import { Box, Grid, ListItem, ListItemText, Typography, Divider, Alert } from '@mui/material';

import {
  LIAM,
  ACCESS_ROLES,
  ASSIGN_ROLES,
  DELETE_ROLES,
  CUSTOMER_IDP,
  ASSIGNED_ROLES,
  AVAILABLE_ROLES,
  EMPTY_LIST_TITLE,
  ACCESS_SUBJECT_ROLES,
  ROLE_INFO_MESSAGE,
  EMPTY_LIST_SUB_TITLE,
  IDP_ROLES,
  IDP_ROLES_SUBTITLE,
} from './constants';
import Loader from '../Loader';
import { useStyles } from './styles';
import { RenderChips } from '../RoleAssignments/RenderChips';
import { ErrorMessage } from '../ErrorMessage/ErrorMessage';
import { compareByProperty } from '../../utility/commonUtility';
import { setIdpRoles } from '../../store/user/userActionCreator';
import { getIdpRoles } from '../../store/user/userSelector';
import interpolateString from '../../utility/stringInterpolation';
import { selectRoleOfSubject } from '../../store/role/roleSelector';
import { getRoleOfSubjectAction } from '../../store/role/roleActionCreator';
import { selectAppLocation } from '../../store/appLocation/appLocationSelector';
import { IBulkRoleAssignments, IRole } from '../../models/Roles/RolesContracts';
import { IErrorMessage } from '../../models/Common/ErrorMessage';
import { setSnackBarMessage } from '../../store/snackBar/snackBarActionCreator';
import { SNACKBAR_ALERT_DEFAULT_PROPS } from '../../container/Machines/constants';
import { selectShowHideRoleAssignmentLoader } from '../../store/loader/loaderSelector';
import { showHideRoleAssignmentLoaderAction } from '../../store/loader/loaderActionCreator';
import { getAllRoles, assignRoleToSubject, getRoleOfSubject, deleteRoleFromSubject } from '../../services/roles';

const RoleAssignments = ({ subjectId, name }: IBulkRoleAssignments) => {
  const { classes } = useStyles();
  const dispatch = useDispatch();

  const selectedRole = useSelector(selectRoleOfSubject);
  const idpRoles = useSelector(getIdpRoles);
  const showLoader = useSelector(selectShowHideRoleAssignmentLoader);
  const appLocation = useSelector(selectAppLocation);

  const [roleList, setRoleList] = useState<any>([]);
  const [isDeleting, setIsDeleting] = useState(false);
  const [totalProgress, setTotalProgress] = useState(0);
  const [currentProgress, setCurrentProgress] = useState(0);
  const [leftSelectedRoles, setLeftSelectedRoles] = useState<any>([]);
  const [assignedRolesList, setAssignedRolesList] = useState<any>([]);
  const [rightSelectedRoles, setRightSelectedRoles] = useState<any>([]);
  const [availableRolesList, setAvailableRolesList] = useState<any>([]);
  const [allowRenderingOfAssignedRoleList, setAllowRenderingOfAssignedRoleList] = useState(false);
  const [errors, setErrors] = useState<IErrorMessage>({ message: '' });

  const getRoles = useCallback(() => {
    dispatch(showHideRoleAssignmentLoaderAction(true));
    getAllRoles(appLocation?.name).then((response: IRole[]) => {
      const filteredResponse = response.filter(role => appLocation || !role.hasAssignedLocations);
      setRoleList(filteredResponse.sort(compareByProperty()));
    }).catch(_error => {
      const errorMessage = {
        message: interpolateString([ACCESS_ROLES])
      };
      setErrors(errorMessage);
    }).finally(() => {
      dispatch(showHideRoleAssignmentLoaderAction(false));
    });
  }, [dispatch, appLocation]);

  const getRolesOfSubject = useCallback((subjectId: string) => {
    setAllowRenderingOfAssignedRoleList(false);
    dispatch(showHideRoleAssignmentLoaderAction(true));
    getRoleOfSubject(subjectId, appLocation?.name).then((response: IRole[]) => {
      const liamRoles = response.filter((role: IRole) => role.associatedFrom === LIAM);
      const idpRoles = response.filter((role: IRole) => role.associatedFrom === CUSTOMER_IDP);
      setAssignedRolesList(liamRoles);
      setAllowRenderingOfAssignedRoleList(true);
      dispatch(getRoleOfSubjectAction(liamRoles));
      dispatch(setIdpRoles(idpRoles));
      dispatch(showHideRoleAssignmentLoaderAction(false));
    }).catch(_error => {
      if (_error.response && _error.response.status === 404) {
        dispatch(getRoleOfSubjectAction([]));
      } else {
        const errorMessage = {
          message: interpolateString([ACCESS_SUBJECT_ROLES])
        };
        setErrors(errorMessage);
      }
    }).finally(() => {
      dispatch(showHideRoleAssignmentLoaderAction(false));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  useEffect(() => {
    setErrors({ message: '' });
    getRoles();
    getRolesOfSubject(subjectId);
  }, [getRoles, getRolesOfSubject, subjectId]);

  useEffect(() => {
    const availableRoles = roleList.filter((role: IRole) => !(_.find(assignedRolesList, { id: role.id })));
    setAvailableRolesList(availableRoles);
  }, [roleList, assignedRolesList]);

  const assignRoles = async (newValue: any) => {
    return new Promise((resolve, reject) => {
      const newRoleToAssign = newValue;
      const newRole = {
        subjectId: subjectId,
        roleId: newRoleToAssign.id.toString()
      };
      assignRoleToSubject(newRole, appLocation?.name).then(_response => {
        dispatch(getRoleOfSubjectAction([...selectedRole, newRoleToAssign]));
        resolve(true);
      }).catch(_error => {
        dispatch(
          setSnackBarMessage({
            ...SNACKBAR_ALERT_DEFAULT_PROPS,
            message: interpolateString([ASSIGN_ROLES]),
            severity: 'error'
          })
        );
        reject();
      });
    })
  };

  const deleteRole = async (role: IRole) => {
    return new Promise((resolve, reject) => {
      deleteRoleFromSubject(subjectId, role.id, appLocation?.name).then(() => {
        dispatch(getRoleOfSubjectAction(selectedRole.filter((entry: IRole) => entry !== role)));
        dispatch(showHideRoleAssignmentLoaderAction(false));
        resolve(true);
      }).catch(_error => {
        dispatch(
          setSnackBarMessage({
            ...SNACKBAR_ALERT_DEFAULT_PROPS,
            message: interpolateString([DELETE_ROLES]),
            severity: 'error'
          })
        );
        reject();
      });
    });
  };

  const filterAssignedOrRemovedRoles = async (items: any) => {
    setCurrentProgress(0);
    const prevAssignedRolesList = [...assignedRolesList];
    const deletedRolesList: any[] = [];
    if (prevAssignedRolesList.length) {
      prevAssignedRolesList.forEach((role: any) => {
        if (!_.find(items, { id: role.id })) {
          deletedRolesList.push(role);
        }
      });
    };
    const addedRolesList = _.pullAllBy(items, prevAssignedRolesList, 'id');
    dispatch(showHideRoleAssignmentLoaderAction(true));
    if (allowRenderingOfAssignedRoleList && deletedRolesList.length) {
      setIsDeleting(true);
      setTotalProgress(deletedRolesList.length);
      deletedRolesList.forEach(async (role: any, index: number) => {
        await deleteRole(role);
        setCurrentProgress(index + 1);
        if ((index + 1) === deletedRolesList.length) {
          getRoles();
          getRolesOfSubject(subjectId);
          setAllowRenderingOfAssignedRoleList(true);
          dispatch(
            setSnackBarMessage({
              ...SNACKBAR_ALERT_DEFAULT_PROPS,
              message: `${deletedRolesList.length} role${deletedRolesList.length > 1 ? 's are' : ' is'} deleted from ${name}`,
              severity: 'success'
            })
          );
        }
      });
    };
    if (addedRolesList.length) {
      setIsDeleting(false);
      setTotalProgress(addedRolesList.length);
      addedRolesList.forEach(async (role: any, index: number) => {
        await assignRoles(role);
        setCurrentProgress(index + 1);
        if ((index + 1) === addedRolesList.length) {
          getRoles();
          getRolesOfSubject(subjectId);
          setAllowRenderingOfAssignedRoleList(true);
          dispatch(
            setSnackBarMessage({
              ...SNACKBAR_ALERT_DEFAULT_PROPS,
              message: `${addedRolesList.length} role${addedRolesList.length > 1 ? 's are' : ' is'} assigned to ${name}`,
              severity: 'success'
            })
          );
        }
      });
    };
    setLeftSelectedRoles([]);
    setRightSelectedRoles([]);
    dispatch(showHideRoleAssignmentLoaderAction(false));
  };
  const onClickListItem = (roleItem: any, setSelected: any) => {
    const { data } = roleItem.item;
    if (_.find(availableRolesList, { id: data.id }) && rightSelectedRoles.length === 0) {
      if (leftSelectedRoles.length < 5) {
        if (_.find(leftSelectedRoles, { id: data.id })) {
          const filteredRoles = leftSelectedRoles.filter((item: any) => item.id !== data.id);
          setLeftSelectedRoles(filteredRoles);
        } else {
          setLeftSelectedRoles([...leftSelectedRoles, data]);
        }
        setSelected(roleItem.item);
      } else if (leftSelectedRoles.length === 5 && _.find(leftSelectedRoles, { id: data.id })) {
        const filteredRoles = leftSelectedRoles.filter((item: any) => item.id !== data.id);
        setLeftSelectedRoles(filteredRoles);
        setSelected(roleItem.item);
      }
    } else if (_.find(assignedRolesList, { id: data.id }) && leftSelectedRoles.length === 0) {
      if (rightSelectedRoles.length < 5) {
        if (_.find(rightSelectedRoles, { id: data.id })) {
          const filteredRoles = rightSelectedRoles.filter((item: any) => item.id !== data.id);
          setRightSelectedRoles(filteredRoles);
        } else {
          setRightSelectedRoles([...rightSelectedRoles, data]);
        }
        setSelected(roleItem.item);
      } else if (rightSelectedRoles.length === 5 && _.find(rightSelectedRoles, { id: data.id })) {
        const filteredRoles = rightSelectedRoles.filter((item: any) => item.id !== data.id);
        setRightSelectedRoles(filteredRoles);
        setSelected(roleItem.item);
      }
    }
  };

  const renderRoleListCollector = useMemo(() => {
    return (
      <>
        <Box className={classes.warningInfoWrap}>
          <Alert
            elevation={0}
            severity={(leftSelectedRoles.length === 5 || rightSelectedRoles.length === 5) ? 'warning' : 'info'}
            variant={'outlined'}
          >
            {ROLE_INFO_MESSAGE}
          </Alert>
        </Box>
        <LuiListCollector
          width={380}
          height={450}
          onLoadSubmit={false}
          itemKey="id"
          allowClearAll
          customMessages={{ clearAll: 'Clear Assigned Roles' }}
          divider={true}
          allowSearch={true}
          isDraggable={true}
          listData={roleList}
          displayNameProperty="name"
          leftListLabel={AVAILABLE_ROLES}
          rightListLabel={ASSIGNED_ROLES}
          defaultSelectedItems={assignedRolesList}
          leftListEmptyMessage={
            <Box className={classes.innerContent} id="empty-component">
              <Inbox fontSize="large" color="action" />
              <Box id="empty-component-text" className={classes.titleAlignment}>
                {EMPTY_LIST_TITLE}
              </Box>
            </Box>
          }
          rightListEmptyMessage={
            <Box className={classes.innerContent} id="empty-component">
              <Inbox fontSize="large" color="action" />
              <Box id="empty-component-text" className={classes.titleAlignment}>
                {EMPTY_LIST_TITLE}
              </Box>
              <Box id="empty-component-title">{EMPTY_LIST_SUB_TITLE}</Box>
            </Box>
          }
          customListItem={(roleItem: any, setSelected: any) => {
            const { data } = roleItem.item;
            return (
              <ListItem
                // button
                key={data.id}
                role="listitem"
                className={classes.roleItem}
                data-testid={`list-item-${data.id}`}
                onClick={() => onClickListItem(roleItem, setSelected)}
              >
                <ListItemText id={data.id} primary={data.name} />
              </ListItem>
            )
          }}
          disableDefaultOnClick={true}
          onSubmit={filterAssignedOrRemovedRoles}
        />
      </>
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roleList, assignedRolesList, leftSelectedRoles, rightSelectedRoles, availableRolesList]);

  return (
    <>
      <Box className={classes.root}>
        {renderRoleListCollector}
        {showLoader ? (
          <Loader showLoader={true} isCenter customClasses={classes.loader} />
        ) : errors ? (
          <ErrorMessage {...errors} />
        ) : null}
      </Box>
      {idpRoles?.length > 0 &&
        <Typography>
          <Grid item xs={12}>
            <Box className={classes.divider}>
              <Divider />
            </Box>
          </Grid>
          <Box className={classes.tabHeader}>{IDP_ROLES}</Box>
          <Box className={classes.idpRolesSubtitle}>{IDP_ROLES_SUBTITLE}</Box>
          <Box className={classes.contentContainer}>
            <RenderChips selectedRole={idpRoles} disabled={false} />
          </Box>
        </Typography>
      }
    </>
  );
};

export default RoleAssignments;
